import React from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { usePatternSidePanelContext } from '../../contexts/PatternSidePanelContext'
import { collection, query, where, orderBy } from 'firebase/firestore'
import { firebase_firestore } from '../../../../common/firebaseConfig'
import { Alert, useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import AlertCard from '../../../alerts/components/AlertCard'
import { useCollection } from 'react-firebase-hooks/firestore'

const AlertsTab: React.FC = () => {
  const { selectedPattern } = usePatternSidePanelContext()
  const { usermeta } = useApplicationSuiteContext()

  // Create a query against the tradingPlans collection
  const alertsQuery = selectedPattern?.id
    ? query(
        collection(firebase_firestore, 'tradingPlans'),
        where('user.gcid', '==', usermeta.gcid),
        where('pattern.id', '==', selectedPattern.id),
        orderBy('active', 'desc')
      )
    : null

  // Use react-firebase-hooks to listen to real-time updates
  const [value, loading, error] = useCollection(alertsQuery)

  // Convert the Firebase documents to Alert objects
  const alerts: Alert[] =
    value?.docs
      .filter((doc) => !(doc.data() as Alert).alertTypes?.includes('exitTradingPlan'))
      .map((doc) => ({
        id: doc.id,
        ...(doc.data() as Omit<Alert, 'id'>),
      })) || []

  if (!selectedPattern) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No pattern selected</Typography>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{`Error loading alerts: ${error.message}`}</Typography>
      </Box>
    )
  }

  function renderNoAlerts() {
    return (
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        height={'100%'}
        width={'100%'}
      >
        <Box display={'flex'} flexDirection={'column'} alignItems={'center'} p={1}>
          <Typography variant="h5" color={'textSecondary'} fontWeight={'normal'}>
            No Alerts found
          </Typography>
          <Typography variant={'body2'} color={'textSecondary'} textAlign={'center'}>
            Create an alert to get notifications when your pattern's rules are met
          </Typography>
        </Box>
      </Box>
    )
  }

  if (alerts.length === 0 && !loading) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: 'transparent',
            borderRadius: '4px',
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'hidden',
            height: '100%',
          }}
        >
          {renderNoAlerts()}
        </Box>
      </>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: 1,
            p: 1,
          }}
        >
          {alerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert as Alert} />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default AlertsTab
